import { useEffect, useRef, useState } from "react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { initiateCafeMenuPayment, getCafeMenuOrderStatus, CafeMenuApiError } from "../cafemenuApi";
import { openRazorpayCheckout } from "../utils/razorpayCheckout";
import { listenToCafeMenuOrderStatus } from "../firebaseOrderStatus";
import { trackCartOrderDone, trackCartConversionCompleted } from "../utils/analytics";

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 15; // ~30s
// The poll above gives up after ~30s, so the "what's happening" notice has to
// appear inside that window (the mobile app waits 2 min because it has no
// such cut-off) — otherwise it could never be seen.
const HIGH_TRAFFIC_TIMEOUT_MS = 15 * 1000;
// If the order still isn't placed when the fast poll gives up, the customer
// is already shown "payment confirmed, still placing" — but we keep watching
// (live push + a slow poll) so that screen upgrades by itself the moment the
// kitchen order lands, instead of staying stale forever.
const SLOW_POLL_INTERVAL_MS = 10000;
const SLOW_POLL_MAX_ATTEMPTS = 90; // ~15 min

/**
 * The whole "pay for this cart" engine, shared by whichever screen hosts the
 * Pay button: starts the payment (Razorpay, or none when nothing is left to pay),
 * then watches the order until the backend reports it placed / failed / refunded.
 */
export default function useCafeMenuCheckout({ cafeId, cafeName }) {
  const {
    items, itemCount, subtotal, taxTotal, grandTotal,
    phoneNumber: cartPhoneNumber, tableNumber, cafeMenuCartId,
  } = useCart();
  const { isAuthenticated, token, phoneNumber: authPhoneNumber, holdSession } = useAuth();
  const phoneNumber = cartPhoneNumber || (authPhoneNumber || "").replace(/\D/g, "").slice(-10);

  const [paying, setPaying] = useState(false);
  const [payStage, setPayStage] = useState(""); // "" | "razorpay" | "confirming"
  const [payError, setPayError] = useState("");
  const [orderResult, setOrderResult] = useState(null); // { invoiceNumber, ristaPushStatus }
  const [showHighTrafficNotice, setShowHighTrafficNotice] = useState(false);
  const [paymentFailedReason, setPaymentFailedReason] = useState("");
  const pollTimerRef = useRef(null);
  const highTrafficTimerRef = useRef(null);
  const firebaseUnsubscribeRef = useRef(null);
  const settledRef = useRef(false);
  const upgradeRef = useRef(false);
  const releaseSessionRef = useRef(null);
  const payInFlightRef = useRef(false);

  useEffect(() => () => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    if (highTrafficTimerRef.current) clearTimeout(highTrafficTimerRef.current);
    if (firebaseUnsubscribeRef.current) firebaseUnsubscribeRef.current();
    if (releaseSessionRef.current) releaseSessionRef.current();
  }, []);

  // Stop every watcher and let go of the session hold.
  const stopWatching = () => {
    upgradeRef.current = false;
    if (releaseSessionRef.current) {
      releaseSessionRef.current();
      releaseSessionRef.current = null;
    }
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    if (highTrafficTimerRef.current) clearTimeout(highTrafficTimerRef.current);
    if (firebaseUnsubscribeRef.current) {
      firebaseUnsubscribeRef.current();
      firebaseUnsubscribeRef.current = null;
    }
  };

  // The order could not be placed and the payment was returned: that's final.
  const showRefunded = () => {
    settledRef.current = true;
    stopWatching();
    setShowHighTrafficNotice(false);
    setPayStage("");
    setPaying(false);
    setPaymentFailedReason("We couldn't place your order, so your payment has been refunded. It reaches your account in 5–7 working days.");
  };

  const upgradeToPlaced = (invoiceNumber) => {
    stopWatching();
    setOrderResult({ invoiceNumber: invoiceNumber || null, ristaPushStatus: "SUCCESS" });
  };

  const slowPollOrderStatus = (cartId, attempt = 0) => {
    if (attempt >= SLOW_POLL_MAX_ATTEMPTS) return;
    pollTimerRef.current = setTimeout(() => {
      getCafeMenuOrderStatus(token, cartId)
        .then((data) => {
          if (!upgradeRef.current) return;
          if (data.ristaPushStatus === "REFUNDED") showRefunded();
          else if (data.ristaPushStatus === "SUCCESS") upgradeToPlaced(data.ristaInvoiceNumber);
          else slowPollOrderStatus(cartId, attempt + 1);
        })
        .catch(() => {
          if (upgradeRef.current) slowPollOrderStatus(cartId, attempt + 1);
        });
    }, SLOW_POLL_INTERVAL_MS);
  };

  const finishOrder = (data) => {
    if (settledRef.current) return;
    settledRef.current = true;
    const placed = data.ristaPushStatus === "SUCCESS";
    if (placed) {
      stopWatching();
    } else {
      // Keep the live listener + session hold; just move to slow polling.
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      if (highTrafficTimerRef.current) clearTimeout(highTrafficTimerRef.current);
      upgradeRef.current = true;
      slowPollOrderStatus(cafeMenuCartId);
    }
    setShowHighTrafficNotice(false);
    setPayStage("");
    setPaying(false);
    setOrderResult({
      invoiceNumber: data.ristaInvoiceNumber || null,
      ristaPushStatus: data.ristaPushStatus || "PENDING",
    });
    const commonParams = {
      cafeId, cafeName, items, itemCount, lineCount: items.length,
      subtotal, taxTotal, grandTotal,
      hasTableNumber: Boolean(tableNumber), hasPhoneNumber: Boolean(phoneNumber),
    };
    trackCartOrderDone(commonParams);
    trackCartConversionCompleted(commonParams);
  };

  const pollOrderStatus = (cartId, attempt = 0) => {
    getCafeMenuOrderStatus(token, cartId)
      .then((data) => {
        if (data.ristaPushStatus === "REFUNDED") { showRefunded(); return; }
        const settled = data.ristaPushStatus === "SUCCESS" || data.ristaPushStatus === "FAILED";
        if (settled || attempt >= POLL_MAX_ATTEMPTS) {
          finishOrder(data);
          return;
        }
        pollTimerRef.current = setTimeout(() => pollOrderStatus(cartId, attempt + 1), POLL_INTERVAL_MS);
      })
      .catch(() => {
        // Payment already succeeded at this point — a status-check hiccup
        // should never look like a failed order. Fall back to "processing".
        finishOrder({ ristaPushStatus: "PENDING" });
      });
  };

  const pay = async () => {
    if (payInFlightRef.current || paying) return;
    if (!isAuthenticated || !token) {
      setPayError("Please verify your mobile number before paying.");
      return;
    }
    if (!cafeMenuCartId) {
      setPayError("Your cart is still saving — please try again in a moment.");
      return;
    }
    setPayError("");
    setPaymentFailedReason("");
    setShowHighTrafficNotice(false);
    settledRef.current = false;
    if (releaseSessionRef.current) releaseSessionRef.current();
    releaseSessionRef.current = holdSession();
    setPaying(true);
    setPayStage("razorpay");
    payInFlightRef.current = true;
    try {
      // The cart (including any applied reward) was already saved and validated
      // server-side — nothing to redo here.
      const payment = await initiateCafeMenuPayment(token, cafeMenuCartId);

      // Shared by the paid (after Razorpay succeeds) and free (nothing to pay)
      // paths: watch the order until the backend reports it placed.
      const startConfirming = () => {
        setPayStage("confirming");
        pollOrderStatus(cafeMenuCartId);

        // Live push, same RTDB path the main app already listens to —
        // short-circuits the poll the moment the backend knows more.
        firebaseUnsubscribeRef.current = listenToCafeMenuOrderStatus(cafeMenuCartId, (data) => {
          if (settledRef.current && !upgradeRef.current) return;
          if (data.status === "placed" || data.status === "completed") {
            if (upgradeRef.current) upgradeToPlaced(data.ristaInvoiceNumber);
            else finishOrder({ ristaPushStatus: "SUCCESS", ristaInvoiceNumber: data.ristaInvoiceNumber });
          } else if (data.status === "failed") {
            // A genuine failure reported after the Razorpay sheet already said
            // "success" must never be silently treated as "still placing".
            settledRef.current = true;
            stopWatching();
            setPaymentFailedReason(data.reason || "Your payment could not be completed.");
          } else if (data.status === "manual_reconciliation" && !upgradeRef.current) {
            setShowHighTrafficNotice(true);
          }
        });

        highTrafficTimerRef.current = setTimeout(() => {
          if (!settledRef.current) setShowHighTrafficNotice(true);
        }, HIGH_TRAFFIC_TIMEOUT_MS);
      };

      // Points / a reward / an offer covered the whole bill: the backend has
      // already placed the order, so there is nothing to pay — no Razorpay.
      if (payment.isFree) {
        startConfirming();
        return;
      }

      await openRazorpayCheckout({
        keyId: payment.keyId,
        amount: payment.amount,
        currency: payment.currency,
        orderId: payment.razorpayOrderId,
        name: cafeName || "Blue Tokai Coffee Roasters",
        description: `Table #${tableNumber}`,
        contact: phoneNumber,
        onSuccess: startConfirming,
      });
    } catch (e) {
      if (releaseSessionRef.current) {
        releaseSessionRef.current();
        releaseSessionRef.current = null;
      }
      const message = e?.message || "";
      if (message.includes("already been checked out")) {
        // This cart was paid in an earlier attempt (tab closed, UPI app
        // switch, retry after a slow confirmation) — show that order's real
        // status instead of an error the customer can't act on.
        setPaying(true);
        setPayStage("confirming");
        settledRef.current = false;
        releaseSessionRef.current = holdSession();
        pollOrderStatus(cafeMenuCartId);
        return;
      }
      if (message === "dismissed") {
        setPayError("Payment cancelled. If money was deducted (e.g. UPI still pending), your order confirms automatically — otherwise you can try again.");
      } else {
        setPayError(e instanceof CafeMenuApiError ? e.message : message || "Something went wrong. Please try again.");
      }
      setPaying(false);
      setPayStage("");
    } finally {
      payInFlightRef.current = false;
    }
  };

  return {
    pay,
    paying,
    payStage,
    payError,
    clearPayError: () => setPayError(""),
    orderResult,
    clearOrderResult: () => setOrderResult(null),
    paymentFailedReason,
    clearPaymentFailed: () => { setPaymentFailedReason(""); setPayError(""); },
    showHighTrafficNotice,
  };
}
